import { useState, useEffect } from 'react';

export default function ArchitectureVisualization() {
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [dataFlowStep, setDataFlowStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  // Auto-animate data flow
  useEffect(() => {
    if (!isAnimating) return;
    const interval = setInterval(() => {
      setDataFlowStep((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, [isAnimating]);

  const dataFlowSteps = [
    { from: 'user', to: 'gateway', label: '메시지 전송', color: 'blue' },
    { from: 'gateway', to: 'storage', label: '컨텍스트 로드', color: 'emerald' },
    { from: 'gateway', to: 'ai', label: '현재 메시지만 전송', color: 'amber' },
    { from: 'ai', to: 'user', label: '응답 수신', color: 'purple' },
  ];

  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-pure-white dark:from-space-black to-gray-50 dark:to-zinc-900/50"></div>
      
      {/* Decorative Lines */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent"></div>
      
      <div className="relative z-10 section-container">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-medium mb-6 border border-blue-200 dark:border-blue-500/20">
            <span>🔄</span>
            <span className="text-blue-700 dark:text-blue-400">데이터 흐름</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-gray-900 dark:from-white to-gray-600 dark:to-gray-300 bg-clip-text text-transparent">
              어디에 무엇이 저장되나요?
            </span>
          </h2>
          
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            moldClaw의 아키텍처를 살펴보세요.<br />
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">당신의 데이터는 당신의 컴퓨터에 있습니다.</span>
          </p>
        </div>

        {/* Architecture Diagram */}
        <div className="max-w-4xl mx-auto">
          {/* Desktop View */}
          <div className="hidden md:block relative bg-gradient-to-br from-gray-50 dark:from-zinc-800/50 to-gray-100 dark:to-zinc-900/50 rounded-3xl p-8 border border-gray-200 dark:border-zinc-700/50">
            {/* Legend */}
            <div className="flex justify-center gap-6 mb-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-gray-600 dark:text-gray-400">로컬 (당신의 컴퓨터)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-gray-600 dark:text-gray-400">외부 (AI 제공자)</span>
              </div>
            </div>

            {/* Diagram Container */}
            <div className="relative h-[400px]">
              {/* Connection Lines - SVG */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
                {/* User to Gateway */}
                <line 
                  x1="20%" y1="35%" x2="40%" y2="35%"
                  className={`stroke-2 transition-all duration-500 ${dataFlowStep === 0 ? 'stroke-blue-500' : 'stroke-gray-300 dark:stroke-zinc-600'}`}
                  strokeDasharray={dataFlowStep === 0 ? "8 4" : "none"}
                >
                  {dataFlowStep === 0 && (
                    <animate attributeName="stroke-dashoffset" from="24" to="0" dur="0.5s" repeatCount="indefinite" />
                  )}
                </line>
                
                {/* Gateway to Storage */}
                <line 
                  x1="50%" y1="50%" x2="50%" y2="75%"
                  className={`stroke-2 transition-all duration-500 ${dataFlowStep === 1 ? 'stroke-emerald-500' : 'stroke-gray-300 dark:stroke-zinc-600'}`}
                  strokeDasharray={dataFlowStep === 1 ? "8 4" : "none"}
                >
                  {dataFlowStep === 1 && (
                    <animate attributeName="stroke-dashoffset" from="24" to="0" dur="0.5s" repeatCount="indefinite" />
                  )}
                </line>
                
                {/* Gateway to AI */}
                <line 
                  x1="60%" y1="35%" x2="80%" y2="35%"
                  className={`stroke-2 transition-all duration-500 ${dataFlowStep === 2 ? 'stroke-amber-500' : 'stroke-gray-300 dark:stroke-zinc-600'}`}
                  strokeDasharray={dataFlowStep === 2 ? "8 4" : "none"}
                >
                  {dataFlowStep === 2 && (
                    <animate attributeName="stroke-dashoffset" from="24" to="0" dur="0.5s" repeatCount="indefinite" />
                  )}
                </line>
                
                {/* AI to User (response) */}
                <path 
                  d="M 80% 45% Q 50% 80% 20% 45%"
                  fill="none"
                  className={`stroke-2 transition-all duration-500 ${dataFlowStep === 3 ? 'stroke-purple-500' : 'stroke-gray-300 dark:stroke-zinc-600'}`}
                  strokeDasharray={dataFlowStep === 3 ? "8 4" : "none"}
                >
                  {dataFlowStep === 3 && (
                    <animate attributeName="stroke-dashoffset" from="100" to="0" dur="1s" repeatCount="indefinite" />
                  )}
                </path>
              </svg>

              {/* User Node */}
              <div 
                className={`absolute left-[5%] top-[25%] w-[15%] transition-all duration-300 cursor-pointer ${activeNode === 'user' ? 'scale-110' : ''}`}
                onMouseEnter={() => { setActiveNode('user'); setIsAnimating(false); }}
                onMouseLeave={() => { setActiveNode(null); setIsAnimating(true); }}
              >
                <div className={`bg-gradient-to-br from-blue-100 dark:from-blue-900/50 to-blue-50 dark:to-blue-950/50 rounded-2xl p-4 border-2 transition-colors ${dataFlowStep === 0 || dataFlowStep === 3 ? 'border-blue-500' : 'border-blue-200 dark:border-blue-800'}`}>
                  <div className="text-3xl mb-2 text-center">💬</div>
                  <div className="text-sm font-semibold text-center text-gray-900 dark:text-white">사용자</div>
                  <div className="text-xs text-center text-gray-500 dark:text-gray-400 mt-1">메신저</div>
                </div>
              </div>

              {/* Gateway Node (Local) */}
              <div 
                className={`absolute left-[35%] top-[20%] w-[30%] transition-all duration-300 cursor-pointer ${activeNode === 'gateway' ? 'scale-105' : ''}`}
                onMouseEnter={() => { setActiveNode('gateway'); setIsAnimating(false); }}
                onMouseLeave={() => { setActiveNode(null); setIsAnimating(true); }}
              >
                <div className="relative">
                  {/* Local Badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10">
                    🖥️ 당신의 컴퓨터
                  </div>
                  <div className={`bg-gradient-to-br from-emerald-100 dark:from-emerald-900/50 to-emerald-50 dark:to-emerald-950/50 rounded-2xl p-6 border-2 transition-colors ${dataFlowStep === 0 || dataFlowStep === 1 || dataFlowStep === 2 ? 'border-emerald-500' : 'border-emerald-200 dark:border-emerald-800'}`}>
                    <div className="text-4xl mb-2 text-center">🏠</div>
                    <div className="text-lg font-bold text-center text-gray-900 dark:text-white">OpenClaw Gateway</div>
                    <div className="text-xs text-center text-emerald-600 dark:text-emerald-400 mt-1">로컬 실행</div>
                  </div>
                </div>
              </div>

              {/* Storage Node (Local) */}
              <div 
                className={`absolute left-[35%] top-[65%] w-[30%] transition-all duration-300 cursor-pointer ${activeNode === 'storage' ? 'scale-105' : ''}`}
                onMouseEnter={() => { setActiveNode('storage'); setIsAnimating(false); }}
                onMouseLeave={() => { setActiveNode(null); setIsAnimating(true); }}
              >
                <div className={`bg-gradient-to-br from-emerald-100 dark:from-emerald-900/50 to-emerald-50 dark:to-emerald-950/50 rounded-2xl p-4 border-2 transition-colors ${dataFlowStep === 1 ? 'border-emerald-500' : 'border-emerald-200 dark:border-emerald-800'}`}>
                  <div className="text-3xl mb-2 text-center">💾</div>
                  <div className="text-sm font-semibold text-center text-gray-900 dark:text-white">로컬 저장소</div>
                  <div className="text-xs text-center text-emerald-600 dark:text-emerald-400 mt-1 font-mono">~/.openclaw/</div>
                </div>
              </div>

              {/* AI Provider Node (External) */}
              <div 
                className={`absolute right-[5%] top-[25%] w-[15%] transition-all duration-300 cursor-pointer ${activeNode === 'ai' ? 'scale-110' : ''}`}
                onMouseEnter={() => { setActiveNode('ai'); setIsAnimating(false); }}
                onMouseLeave={() => { setActiveNode(null); setIsAnimating(true); }}
              >
                <div className="relative">
                  {/* External Badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10 whitespace-nowrap">
                    ⚠️ 외부
                  </div>
                  <div className={`bg-gradient-to-br from-amber-100 dark:from-amber-900/50 to-amber-50 dark:to-amber-950/50 rounded-2xl p-4 border-2 transition-colors ${dataFlowStep === 2 || dataFlowStep === 3 ? 'border-amber-500' : 'border-amber-200 dark:border-amber-800'}`}>
                    <div className="text-3xl mb-2 text-center">🤖</div>
                    <div className="text-sm font-semibold text-center text-gray-900 dark:text-white">AI API</div>
                    <div className="text-xs text-center text-gray-500 dark:text-gray-400 mt-1">Anthropic 등</div>
                  </div>
                </div>
              </div>

              {/* Flow Step Indicator */}
              <div className="absolute bottom-0 left-0 right-0">
                <div className="flex justify-center items-center gap-4">
                  {dataFlowSteps.map((step, index) => (
                    <button
                      key={index}
                      onClick={() => { setDataFlowStep(index); setIsAnimating(false); }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                        dataFlowStep === index 
                          ? `bg-${step.color}-500 text-white shadow-lg` 
                          : 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-zinc-700'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${dataFlowStep === index ? 'bg-white animate-pulse' : 'bg-gray-400'}`}></span>
                      <span className="hidden sm:inline">{step.label}</span>
                      <span className="sm:hidden">{index + 1}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile View - Simplified */}
          <div className="md:hidden space-y-4">
            {/* Flow Steps */}
            <div className="space-y-3">
              {dataFlowSteps.map((step, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-2xl border transition-all ${
                    dataFlowStep === index 
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30' 
                      : 'border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/50'
                  }`}
                  onClick={() => setDataFlowStep(index)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'bg-blue-500' :
                      index === 1 ? 'bg-emerald-500' :
                      index === 2 ? 'bg-amber-500' :
                      'bg-purple-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">{step.label}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {index === 0 && '메신저 → Gateway'}
                        {index === 1 && 'Gateway ↔ 로컬 저장소'}
                        {index === 2 && 'Gateway → AI API (현재 메시지만)'}
                        {index === 3 && 'AI API → 사용자'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-8 bg-gradient-to-r from-emerald-50 dark:from-emerald-900/20 to-cyan-50 dark:to-cyan-900/20 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-800/50">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                <span className="text-lg">💡</span>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">핵심 포인트</h4>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong className="text-gray-900 dark:text-white">대화 기록</strong>은 당신의 컴퓨터 <code className="bg-gray-100 dark:bg-zinc-800 px-1 rounded">~/.openclaw/</code>에만 저장됩니다</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong className="text-gray-900 dark:text-white">AI 서버</strong>에는 현재 메시지만 전송됩니다 (세션 없음)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span><strong className="text-gray-900 dark:text-white">moldClaw 서버</strong>에는 대화 내용이 저장되지 않습니다</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
